<template>
  <div class="sdmc">
    <!-- ── Pathways ───────────────────────────────────────────────── -->
    <!-- The learner's pathways, exactly as the old My Pathways page showed
         them. Hidden, not unmounted, when there are none, so the child still
         fetches and can say how many it found. -->
    <section v-show="pathwayCount > 0" class="sdmc-group sdmc-group--pathways">
      <button type="button" class="sdmc-group__head" :aria-expanded="isOpen(pathwaysKey) ? 'true' : 'false'"
        @click="toggle(pathwaysKey)">
        <span class="sdmc-group__chevron" :class="{ 'is-open': isOpen(pathwaysKey) }" aria-hidden="true">
          <svg viewBox="0 0 20 20" width="18" height="18" fill="currentColor"><path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.17 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clip-rule="evenodd" /></svg>
        </span>
        <span class="sdmc-group__title">Pathways</span>
        <span class="sdmc-group__meta">
          <span class="sdmc-pill sdmc-pill--pathway">Pathway</span>
          {{ pathwayCount }} {{ pathwayCount === 1 ? 'pathway' : 'pathways' }}
        </span>
      </button>

      <div v-show="isOpen(pathwaysKey)" class="sdmc-group__body sdmc-group__body--flush">
        <safe-dig-my-learning @loaded="n => (pathwayCount = n)" />
      </div>
    </section>

    <!-- ── Individual courses ─────────────────────────────────────── -->
    <section class="sdmc-group sdmc-group--individual">
      <button type="button" class="sdmc-group__head" :aria-expanded="isOpen(individualKey) ? 'true' : 'false'"
        @click="toggle(individualKey)">
        <span class="sdmc-group__chevron" :class="{ 'is-open': isOpen(individualKey) }" aria-hidden="true">
          <svg viewBox="0 0 20 20" width="18" height="18" fill="currentColor"><path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.17 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clip-rule="evenodd" /></svg>
        </span>
        <span class="sdmc-group__title">Courses</span>
        <span class="sdmc-group__meta">
          <span class="sdmc-pill sdmc-pill--individual">Course</span>
          {{ individualPassed }} of {{ individualTotal }} passed
        </span>
      </button>

      <div v-show="isOpen(individualKey)" class="sdmc-group__body">
        <div v-if="individualCourses.length === 0" class="sdmc-empty">
          {{ statusFilter === null ? 'No courses have been assigned to you directly.' : 'No ' + statusLabel(statusFilter).toLowerCase() + ' individual courses.' }}
        </div>
        <article v-for="course in individualCourses" :key="'ec-' + course.employee_course_id" class="sdmc-row">
          <img class="sdmc-row__img" :src="course.img || '/placeholder.jpg'" alt="" loading="lazy" />
          <div class="sdmc-row__main">
            <h5 class="sdmc-row__name">{{ course.course_name }}</h5>
            <p class="sdmc-row__dates">
              <span>Assigned: {{ fmt(course.assigned_date) }}</span>
              <span v-if="course.course_status !== 1">Due: {{ fmt(course.due_date) }}</span>
              <span v-else>Completed: {{ fmt(course.completion_date) }}</span>
              <span v-if="course.course_length">{{ course.course_length }} minutes</span>
            </p>
            <p v-if="course.previous_completion_note" class="sdmc-row__note sdmc-row__note--prior">
              <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clip-rule="evenodd" /></svg>
              {{ course.previous_completion_note }}
            </p>
          </div>
          <span class="sdmc-status" :class="'sdmc-status--' + course.course_status">{{ statusLabel(course.course_status) }}</span>
          <div class="sdmc-row__actions">
            <template v-if="course.course_status === 1 && course.certificates && course.certificates.length">
              <template v-for="(certificate, ci) in course.certificates">
                <base-button v-if="certificate.manual == 0 && certificate.is_proctored_exam == 0" :key="'pv-' + ci"
                  type="primary" outline="true" size="sm" @click.prevent="$emit('preview-certificate', course, certificate)">Certificate</base-button>
                <base-button v-else-if="certificate.manual == 1" :key="'pm-' + ci"
                  type="primary" outline="true" size="sm" @click.prevent="$emit('preview-manual', certificate)">Certificate</base-button>
                <base-button v-else-if="certificate.is_proctored_exam == 1" :key="'pp-' + ci"
                  type="primary" outline="true" size="sm" @click.prevent="$emit('proctored-certificate', certificate)">Certificate</base-button>
                <base-button v-if="certificate.manual == 0 && certificate.is_proctored_exam == 0" :key="'dl-' + ci"
                  type="primary" outline="true" size="sm" @click.prevent="$emit('download-certificate', course, certificate)">Download</base-button>
              </template>
            </template>
            <base-button v-else-if="payByEmployeeOn && !course.is_employee_paid" type="primary" outline="true" size="sm"
              @click.prevent="$emit('pay', course)">Pay</base-button>
            <base-button v-else type="primary" outline="true" size="sm" @click.prevent="$emit('take', course)">
              {{ takeLabel(course) }}
            </base-button>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import moment from 'moment';
import SafeDigMyLearning from '@/views/Pages/safe-dig-indiana/MyLearning.vue';

/**
 * Safe Dig's My Courses & Pathways body: the learner's pathways on top, in
 * the same shape the old My Pathways page used, and their courses below.
 *
 * Every course row is one assignment occurrence (one tbl_employee_courses
 * row), never a course id: the copy of a course a pathway assigned and a copy
 * an admin assigned directly are two rows with their own dates, status and
 * P/O/F result. The pathway copies belong to their pathway and are listed
 * under it; the Courses section holds the direct copies, with a note of where
 * the learner completed the course before, if they have.
 *
 * The parent (EmployeeCourses.vue) owns the course data and the course
 * actions; the pathways half fetches its own.
 */
export default {
  name: 'SafeDigMyCourses',
  components: { SafeDigMyLearning },
  props: {
    courses: { type: Array, default: () => [] },
    /** employee_course_status to show, or null for all. */
    statusFilter: { type: Number, default: null },
    payByEmployeeOn: { type: [Boolean, Number], default: false },
  },
  data() {
    return {
      collapsed: {},
      // The keys the two sections are collapsed under. Strings, so they can
      // never collide with a pathway id.
      individualKey: 'individual',
      pathwaysKey: 'pathways',
      // How many pathways the child found; 0 hides the section.
      pathwayCount: 0,
    };
  },
  computed: {
    filtered() {
      return this.statusFilter === null ? this.courses : this.courses.filter(c => c.course_status === this.statusFilter);
    },
    individualCourses() {
      return this.filtered.filter(c => c.assignment_source !== 'pathway');
    },
    /** Counted over every direct course, not just the ones the filter shows. */
    individualTotal() {
      return this.courses.filter(c => c.assignment_source !== 'pathway').length;
    },
    individualPassed() {
      return this.courses.filter(c => c.assignment_source !== 'pathway' && c.course_status === 1).length;
    },
  },
  methods: {
    isOpen(id) { return !this.collapsed[id]; },
    toggle(id) { this.$set(this.collapsed, id, !this.collapsed[id]); },
    statusLabel(s) { return { 0: 'Fail', 1: 'Pass', 2: 'Open', 3: 'Overdue' }[s] || 'Open'; },
    takeLabel(course) {
      if (course.course_status === 1) return 'Review Course';
      return course.course_attempts && course.course_attempts.length > 0 ? 'Retake Course' : 'Take Course';
    },
    fmt(v) {
      if (!v) return 'N/A';
      const m = moment(v);
      return m.isValid() ? m.format('MM-DD-YYYY') : v;
    },
  },
};
</script>

<style scoped>
.sdmc { --sd-accent: #3e7799; --sd-navy: #1e3a5f; --sd-muted: #64748b; --sd-border: #e2e8f0; }
.sdmc-group { border: 1px solid var(--sd-border); border-radius: 12px; background: #fff; margin-bottom: 1rem; overflow: hidden; }
.sdmc-group__head { display: flex; align-items: center; gap: 0.75rem; width: 100%; text-align: left; background: #f8fafc; border: 0; border-bottom: 1px solid var(--sd-border); padding: 0.85rem 1rem; cursor: pointer; }
.sdmc-group__chevron { display: inline-flex; color: var(--sd-muted); transition: transform 0.15s; }
.sdmc-group__chevron.is-open { transform: rotate(90deg); }
.sdmc-group__title { font-size: 1.02rem; font-weight: 700; color: var(--sd-navy); flex: 1; min-width: 0; }
.sdmc-group__meta { display: inline-flex; align-items: center; gap: 0.6rem; font-size: 0.82rem; color: var(--sd-muted); white-space: nowrap; }
.sdmc-link { color: var(--sd-accent); font-weight: 600; text-decoration: none; }
.sdmc-link:hover { text-decoration: underline; }
.sdmc-pill { display: inline-block; padding: 0.1rem 0.5rem; border-radius: 999px; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
.sdmc-pill--pathway { background: #e0e7ff; color: var(--sd-navy); }
.sdmc-pill--individual { background: #e2e8f0; color: var(--sd-navy); }
.sdmc-group__body { padding: 0.25rem 0; }
.sdmc-group__body--flush { padding: 1rem; background: #fbfdff; }
.sdmc-group__body--flush >>> .sdl-section { margin-bottom: 0; }
.sdmc-empty { padding: 1.25rem 1rem; color: var(--sd-muted); font-size: 0.9rem; }
.sdmc-row { display: grid; grid-template-columns: 64px 1fr auto auto; align-items: center; gap: 1rem; padding: 0.8rem 1rem; border-bottom: 1px solid #f1f5f9; }
.sdmc-row:last-child { border-bottom: 0; }
.sdmc-row__img { width: 64px; height: 44px; object-fit: cover; border-radius: 8px; background: #f1f5f9; }
.sdmc-row__main { min-width: 0; }
.sdmc-row__name { margin: 0 0 0.2rem; font-size: 0.95rem; font-weight: 600; color: #0f172a; }
.sdmc-row__dates { margin: 0; font-size: 0.78rem; color: var(--sd-muted); display: flex; flex-wrap: wrap; gap: 0.3rem 1rem; }
.sdmc-row__note { margin: 0.25rem 0 0; font-size: 0.78rem; color: var(--sd-accent); display: inline-flex; align-items: center; gap: 0.3rem; }
.sdmc-row__note--prior { color: #92400e; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 0.15rem 0.5rem; }
.sdmc-status { display: inline-block; min-width: 76px; text-align: center; padding: 0.2rem 0.6rem; border-radius: 999px; font-size: 0.78rem; font-weight: 700; }
.sdmc-status--1 { background: #dcfce7; color: #15803d; }
.sdmc-status--2 { background: #fef9c3; color: #854d0e; }
.sdmc-status--0 { background: #fee2e2; color: #b91c1c; }
.sdmc-status--3 { background: #fee2e2; color: #b91c1c; }
.sdmc-row__actions { display: flex; gap: 0.4rem; flex-wrap: wrap; justify-content: flex-end; }
@media (max-width: 767px) {
  .sdmc-row { grid-template-columns: 1fr auto; }
  .sdmc-row__img { display: none; }
  .sdmc-row__actions { grid-column: 1 / -1; justify-content: flex-start; }
  .sdmc-group__meta { white-space: normal; }
}
</style>
